/**
 * Front-end JavaScript
 *
 * The JavaScript code you place here will be processed by esbuild. The output
 * file will be created at `../theme/js/script.min.js` and enqueued in
 * `../theme/functions.php`.
 *
 * For esbuild documentation, please see:
 * https://esbuild.github.io/
 */

import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import { focus } from '@alpinejs/focus';

import { initProductFilters } from './modules/ajax-product-filters';
import { notificationManager } from './modules/notification-manager';
import './modules/component-dropdown';
import { erdFloatingCartCounter } from './modules/ajax-cart-counter';
import { customCursorAnimation } from './modules/cursor';
import { productQtyButtons } from './modules/product-qty-input';
import { productVariationDropdown } from './modules/product-variation-dropdowns';
import { singleAjaxAddToCart } from './modules/ajax-add-to-cart';
import { initAnimations } from './modules/animations';
import { productStockNotificationForm } from './modules/product-notify-form';
import { productMainPriceUpdater } from './modules/product-price-updater';
import { ajaxCartCouponForm } from './modules/ajax-cart-coupon';
import { ajaxCartQuantity } from './modules/ajax-cart-qty';
import { ajaxRemoveCart } from './modules/ajax-cart-remove';
import { initSwiper } from './modules/swiper';
import { initTooltip } from './modules/tooltip';
import { ajaxFilterNews } from './modules/ajax-news';
import { initRegisterForm } from './modules/register-form';
import { SkeletonLoader } from './modules/skeleton-loader-class';
import { initHeaderScroll } from './modules/header';
import { initGalleryCarousel } from './modules/gallery-carousel';
import './modules/contact-form';
import './modules/yt';
import handleScrollTo from './modules/scroll-to';
import Lenis from 'lenis';
import gsap from 'gsap';
import { handleCookieCheckboxes } from './modules/cookie-notice';
import './modules/faq-link';
import './modules/select';
import './modules/animations/cart-button';

const lenis = new Lenis({
	autoRaf: true,
	lerp: 0.1,
	allowNestedScroll: true,
});

const nestedLenisScroll = () => {
	const nestedTargets = document.querySelectorAll(
		'[data-nested-lenis="wrapper"]'
	);
	if (!nestedTargets.length) return;

	nestedTargets.forEach((wrapper) => {
		const content = wrapper.querySelector('[data-nested-lenis="content"]');
		const isMobile = window.matchMedia('(max-width: 768px)').matches;

		// Skip Lenis initialization on showroom mobile
		if (
			isMobile &&
			wrapper.getAttribute('data-disable-lenis') === 'mobile'
		) {
			return;
		}

		const nestedLenis = new Lenis({
			wrapper: wrapper,
			content: content,
			lerp: 0.1,
			overscroll: false,
			allowNestedScroll: true,
		});

		gsap.ticker.add((time) => {
			if (nestedLenis) {
				nestedLenis.raf(time * 1000);
			}
		});
	});
};

const initCkyPreferenceLenis = () => {
	const ckyWrapper = document.querySelector('.cky-preference-body-wrapper');
	if (!ckyWrapper) return;

	const ckyLenis = new Lenis({
		wrapper: ckyWrapper,
		content: ckyWrapper.firstElementChild,
		lerp: 0.1,
		overscroll: false,
		allowNestedScroll: true,
	});

	gsap.ticker.add((time) => {
		if (ckyLenis) {
			ckyLenis.raf(time * 1000);
		}
	});
};

const initAlgoliaLenis = () => {
	setTimeout(() => {
		const algoliaWrapper = document.querySelectorAll(
			'.algolia-autocomplete'
		);

		if (!algoliaWrapper.length) return;

		algoliaWrapper.forEach((wrapper) => {
			const algoliaLenis = new Lenis({
				wrapper: wrapper,
				content: wrapper.firstElementChild,
				lerp: 0.1,
				overscroll: false,
				allowNestedScroll: true,
			});

			gsap.ticker.add((time) => {
				if (algoliaLenis) {
					algoliaLenis.raf(time * 1000);
				}
			});
		});
	}, 1000);
};

/**
 * Init alpine JS
 */
window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.plugin(focus);
Alpine.start();

const handleContactForm = () => {
	document
		.querySelectorAll('.wpcf7-form [aria-required="true"]')
		.forEach((field) => {
			const grandparent = field.parentElement?.parentElement;
			if (grandparent) {
				const label = grandparent.querySelector('label');
				if (label) {
					label.insertAdjacentHTML(
						'beforeend',
						'<span class="required">*</span>'
					);
				}
			}
		});
};

const init = () => {
	// Initializing and hiding skeleton loaders
	const productSkeleton = new SkeletonLoader(
		'[data-skeleton="product-card"]',
		'li.product'
	);
	productSkeleton.hide();

	initSwiper();
	initProductFilters();
	erdFloatingCartCounter();
	customCursorAnimation();
	productQtyButtons();
	productVariationDropdown();
	singleAjaxAddToCart();
	initAnimations();
	productStockNotificationForm();
	productMainPriceUpdater();
	ajaxCartCouponForm();
	ajaxCartQuantity();
	ajaxRemoveCart();
	ajaxFilterNews();
	initTooltip();
	initRegisterForm();
	initHeaderScroll();
	initGalleryCarousel();
	handleContactForm();
	handleScrollTo();
	nestedLenisScroll();
	initCkyPreferenceLenis();
	initAlgoliaLenis();
	handleCookieCheckboxes();
};

// Wait for the DOM to be fully loaded
document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('#search-form').forEach((form) => {
		form.addEventListener('submit', (e) => {
			e.preventDefault();
		});
	});

	// Initialize the script
	init();

	// Also watch for dynamically added CKY elements
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			mutation.addedNodes.forEach((node) => {
				if (
					node.nodeType === 1 &&
					(node.classList?.contains('cky-preference-body-wrapper') ||
						node.querySelector?.('.cky-preference-body-wrapper'))
				) {
					initCkyPreferenceLenis();
				}
			});
		});
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
});
